import { createSelector } from '@reduxjs/toolkit';

import { ProductSetInventory, ProductSetInventoryStatus } from 'shared';
import { RootState } from 'store/store';

export const selectProductSets = (state: RootState) => state.FurnitureAndEquipment.productSets;
export const selectBaseProductSets = (state: RootState) => state.FurnitureAndEquipment.baseProductSets;
export const selectProductSetCategories = (state: RootState) =>
  state.FurnitureAndEquipment.baseProductSets?.map(bps => bps.category);
export const selectProductCart = (state: RootState) => state.FurnitureAndEquipment.productCart;
export const selectProductSetOrders = (state: RootState) => state.FurnitureAndEquipment.productSetOrders;
export const selectIsCartToastVisible = (state: RootState) => state.FurnitureAndEquipment.isCartToastVisible;
export const selectCartToastTimeoutId = (state: RootState) => state.FurnitureAndEquipment.cartToastTimeoutId;
export const selectProductSetInventory = (state: RootState) => state.FurnitureAndEquipment.productSetInventory;
export const selectHasFurnitureAndEquipment = (state: RootState) => {
  const inventory = state.FurnitureAndEquipment.productSetInventory;
  if (!inventory) return false;
  const items: ProductSetInventory[] = Array.isArray(inventory)
    ? inventory
    : (Object.values(inventory) as ProductSetInventory[]);
  return items.length > 0;
};
export const selectHasCompletedFurnitureAndEquipment = (state: RootState) => {
  const inventory = state.FurnitureAndEquipment.productSetInventory;
  if (!inventory) return false;
  const items: ProductSetInventory[] = Array.isArray(inventory)
    ? inventory
    : (Object.values(inventory) as ProductSetInventory[]);
  return items.some(item => (item.status ?? 'completed') === 'completed');
};
export const selectProductSetInventoryQuantity = (baseProductSetId: number) =>
  createSelector(selectProductSetInventory, inventory => {
    if (!inventory) return 0;
    const items: ProductSetInventory[] = Array.isArray(inventory)
      ? inventory
      : (Object.values(inventory) as ProductSetInventory[]);
    return items
      .filter(item => item.product_set.base_product_set_id === baseProductSetId)
      .reduce((acc, item) => acc + item.rental_quantity + item.purchase_quantity, 0);
  });
export const selectProductSetInventoryQuantityByStatus = (
  baseProductSetId: number,
  status: ProductSetInventoryStatus
) =>
  createSelector(selectProductSetInventory, inventory => {
    if (!inventory) return 0;
    const items: ProductSetInventory[] = Array.isArray(inventory)
      ? inventory
      : (Object.values(inventory) as ProductSetInventory[]);
    const filteredByBaseProductSet = items.filter(item => item.product_set.base_product_set_id === baseProductSetId);
    const filteredByStatus = filteredByBaseProductSet.filter(item =>
      status === 'completed' ? (item.status ?? 'completed') === 'completed' : item.status === status
    );
    return filteredByStatus.reduce((acc, item) => acc + item.rental_quantity + item.purchase_quantity, 0);
  });
export const selectDeliveredProductSetOrders = (state: RootState) => {
  const productSetOrders = selectProductSetOrders(state);
  return productSetOrders?.filter(order =>
    order.product_set_rental_groups.every(group => group.status === 'completed')
  );
};
export const selectNonDeliveredProductSetOrders = (state: RootState) => {
  const productSetOrders = selectProductSetOrders(state);
  return productSetOrders?.filter(order =>
    order.product_set_rental_groups.some(group => group.status === 'pending' || group.status === 'scheduled')
  );
};
export const selectSalesTaxRate = (state: RootState) => state.FurnitureAndEquipment.productCart?.sales_tax_rate;
export const selectTenancyServices = (state: RootState) => state.Tenancy.tenancy_services;
